import { observeProductRequest } from '@/lib/observability/product-request'
/**
 * Centralized "start a Stripe checkout, with GTM tracking" helper.
 *
 * Every surface that hands the user off to Stripe Checkout MUST go through
 * this helper instead of calling `startPlanCheckoutAction` directly. It
 * guarantees three things happen, in order, before the redirect:
 *
 *   1. Stripe creates a real Checkout Session.
 *   2. The `gtm_pending_checkout_selection` shuttle is written to
 *      `sessionStorage` for cancellation attribution and browser-vs-Stripe
 *      diagnostics. Successful purchase payloads come from verified Stripe
 *      Session data, so blocked storage does not drop the conversion.
 *   3. `brew_checkout_started` fires with the Stripe Session ID as its stable
 *      transaction ID, then the redirect URL is returned to the caller.
 *
 * Historically only `components/billing/plan-grid.tsx` did all three; the
 * in-app upgrade-limit dialog (`contexts/upgrade-limit-context.tsx`)
 * skipped them, silently dropping every upsell-driven conversion event.
 * Routing all callers through this helper makes that class of bug
 * impossible.
 *
 * Wire-shape note: do not change event field names or types here without
 * coordinating with the GTM container (Custom Event triggers + Data Layer
 * Variables expect today's keys).
 */

import { startPlanCheckoutAction } from '@/lib/actions/billing'
import type { SelfServePlanKey } from '@/lib/billing/plans'
import type { BillingCadence } from '@/lib/billing/promo'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { trackCheckoutStarted, trackSubscriptionChange } from '@/lib/site/gtm'
import { writePendingCheckoutSelection } from '@/lib/site/pending-checkout'

export type StartTrackedCheckoutArgs = {
  /** Plan the user is buying. */
  planKey: SelfServePlanKey
  /** Display name for the plan, used in the GTM payload. */
  planName: string
  /** Price in cents, used both in the pending blob and divided to dollars for GTM. */
  planPriceCents: number
  /** ISO 4217 currency code for the price. */
  planPriceCurrency: string
  /** Authenticated Clerk user id, or null/undefined for anonymous (skips the GTM event). */
  userId: string | null | undefined
  /** Authenticated Clerk org id, optional. */
  orgId: string | null | undefined
  /**
   * How the buyer chose to pay. `commit` = the 6-month prepaid term; omit or
   * `monthly` for the ordinary month-to-month subscription. `planPriceCents`
   * must already reflect what Stripe will actually charge for that cadence
   * (the whole term for `commit`), so the GTM value matches `amount_total`.
   */
  cadence?: BillingCadence
  /**
   * Display name of the org's CURRENT plan at click time. Stashed in the
   * pending blob so the post-checkout tracker can pick `subscription_started`
   * (Free → Paid) vs `subscription_upgraded` (Paid → Paid) without re-reading
   * billing state on the return route.
   *
   * Falls back to `'free'` when omitted, matching the prior `plan-grid.tsx`
   * behavior.
   */
  currentPlanName: string | null | undefined
  /**
   * Same-origin path Stripe returns to when the buyer backs out of Checkout
   * (defaults to `/plan?billing=canceled`). The onboarding wizard passes its
   * session-scoped `/setup` URL so a cancel resumes the wizard.
   */
  cancelPath?: string
  /**
   * Same-origin path opened after `/plan` verifies the completed Checkout.
   */
  successPath?: string
}

export type StartTrackedCheckoutResult =
  | { ok: true; url: string }
  | { ok: false; error: string }

export type InAppPlanUpgradeStartArgs = {
  userId: string | null | undefined
  orgId: string | null | undefined
  priorPlanName: string | null | undefined
  planKey: SelfServePlanKey
  planName: string
  price: { amountCents: number; currency: string } | null | undefined
}

/**
 * Track + persist + start a Stripe checkout. Returns the Stripe redirect URL.
 *
 * Caller is responsible for performing the actual navigation. Callers that
 * open Checkout in a new tab must synchronously reserve that tab from the
 * click gesture before awaiting this helper, then navigate the reserved tab
 * with the returned URL.
 */
export async function startTrackedCheckout(
  args: StartTrackedCheckoutArgs
): Promise<StartTrackedCheckoutResult> {
  const currency = args.planPriceCurrency || 'USD'
  const normalizedCurrency = currency.toUpperCase()
  const priorPlanName = args.currentPlanName ?? 'free'

  // Do not count a click as checkout_started. Stripe must first return a real
  // session so failed session creation is excluded and we have a stable ID.
  try {
    const returnOptions =
      args.cancelPath === undefined && args.successPath === undefined
        ? undefined
        : {
            ...(args.cancelPath !== undefined
              ? { cancelPath: args.cancelPath }
              : {}),
            ...(args.successPath !== undefined
              ? { successPath: args.successPath }
              : {}),
          }
    const { url, sessionId } = await observeProductRequest(
      'billing.checkout_session',
      () =>
        startPlanCheckoutAction(
          args.planKey,
          args.cadence ?? 'monthly',
          returnOptions
        ),
      {
        context: {
          userId: args.userId ?? undefined,
          orgId: args.orgId ?? undefined,
        },
        resource: args.planKey,
        trigger: args.cadence ?? 'monthly',
      }
    )
    writePendingCheckoutSelection({
      checkoutSessionId: sessionId,
      planKey: args.planKey,
      planName: args.planName,
      planPriceCents: args.planPriceCents,
      planPriceCurrency: currency,
      priorPlanName,
    })

    if (args.userId) {
      const price = args.planPriceCents / 100
      await trackCheckoutStarted({
        transaction_id: sessionId,
        user_id: args.userId,
        org_id: args.orgId ?? undefined,
        plan_id: args.planKey,
        plan_name: args.planName,
        plan_price: price,
        value: price,
        currency: normalizedCurrency,
        current_plan: args.currentPlanName ?? undefined,
      })
    }

    return { ok: true, url }
  } catch (error) {
    return {
      ok: false,
      error: getErrorMessage(error, 'Failed to start checkout.'),
    }
  }
}

const UPGRADE_CHECKOUT_TRACKED_PREFIX = 'gtmUpgradeCheckoutStarted'

function hasTrackedUpgradeCheckout(sessionKey: string): boolean {
  try {
    return window.sessionStorage.getItem(sessionKey) === '1'
  } catch {
    return false
  }
}

function markUpgradeCheckoutTracked(sessionKey: string): void {
  try {
    window.sessionStorage.setItem(sessionKey, '1')
  } catch {
    // Storage blocked: the day-stable transaction_id below still lets
    // Google Ads dedupe repeats via orderId.
  }
}

/**
 * Track the paid-to-paid upgrade intent before the in-app Stripe reprice runs.
 * Paid orgs do not visit Stripe Checkout, but the product lifecycle still has
 * a distinct begin-checkout step when the user confirms the charge.
 *
 * Counting rules (2026-08 GTM audit): this used to mint a fresh
 * `Date.now()` transaction id on every confirm click and also fired for
 * repriceings that then failed — inflating begin_checkout / InitiateCheckout
 * on every ad platform with IDs nothing could dedupe. Now the id is stable
 * per (org, plan, UTC day) so Google Ads' orderId dedup collapses same-day
 * repeats, and a sessionStorage once-guard stops same-session repeat
 * confirms from emitting at all.
 */
export function trackInAppPlanUpgradeStarted(
  args: InAppPlanUpgradeStartArgs
): Promise<void> {
  if (!args.userId || !args.price) {
    return Promise.resolve()
  }

  const attributionId = args.orgId ?? args.userId
  const sessionKey = `${UPGRADE_CHECKOUT_TRACKED_PREFIX}:${attributionId}:${args.planKey}`
  if (hasTrackedUpgradeCheckout(sessionKey)) {
    return Promise.resolve()
  }
  markUpgradeCheckoutTracked(sessionKey)

  const price = args.price.amountCents / 100
  const dayUtc = new Date(Date.now()).toISOString().slice(0, 10)
  return trackCheckoutStarted({
    transaction_id: `upgrade-checkout-${attributionId}-${args.planKey}-${dayUtc}`,
    user_id: args.userId,
    org_id: args.orgId ?? undefined,
    plan_id: args.planKey,
    plan_name: args.planName,
    plan_price: price,
    value: price,
    currency: (args.price.currency || 'USD').toUpperCase(),
    current_plan: args.priorPlanName ?? undefined,
  })
}

/**
 * Fire the GTM conversion event for an IN-APP plan change (no Stripe redirect).
 *
 * In-app upgrades reprice the existing subscription and never pass through the
 * `?billing=success` `BillingSuccessTracker`, so the conversion would otherwise
 * be dropped. Mirrors that tracker by delegating to `trackSubscriptionChange`,
 * which picks `brew_subscription_started` vs `brew_subscription_upgraded` from
 * the prior plan and carries the paid Stripe Invoice ID and amount.
 *
 * No-ops unless this is an `upgrade` (downgrades and idempotent no-ops are not
 * conversions), we have a `userId`, and we have a price. Fire-and-forget — the
 * surface stays mounted, so there's no navigation that could drop the event.
 */
export function trackInAppPlanUpgrade(args: {
  direction: 'upgrade' | 'downgrade' | 'none' | 'kept'
  userId: string | null | undefined
  orgId: string | null | undefined
  /** Display name of the plan the org is on BEFORE the change. */
  priorPlanName: string | null | undefined
  planKey: SelfServePlanKey
  planName: string
  price: { amountCents: number; currency: string } | null | undefined
  conversion:
    | { transactionId: string; value: number; currency: string }
    | null
    | undefined
}): void {
  if (
    args.direction !== 'upgrade' ||
    !args.userId ||
    !args.price ||
    !args.conversion
  ) {
    return
  }

  void trackSubscriptionChange({
    user_id: args.userId,
    org_id: args.orgId ?? undefined,
    active_plan_name: args.priorPlanName ?? 'free',
    transaction_id: args.conversion.transactionId,
    transaction_value: args.conversion.value,
    transaction_currency: args.conversion.currency,
    new_plan: {
      id: args.planKey,
      name: args.planName,
      fee: { amount: args.price.amountCents, currency: args.price.currency },
    },
  })
}
